import { computed, effect, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, timeout } from 'rxjs';
import { ApiService } from './api-data.service';
import { URLS } from '../_config/api.config';

// Ported from customer-website's ThemeConfigurationService: fetches
// color overrides from the backend and syncs them onto the --* CSS
// custom properties used by the login/registration shell, at runtime. Falls
// back to today's static colors (login-design-tokens.scss) when the
// endpoint has nothing for a key, errors, or doesn't exist yet.
const DEFAULT_THEME: Record<string, string> = {
	'primary-color': '#0066E4',
	'primary-font-color': '#181D27',
	'secondary-grey-color': '#535862',
	'dark-grey-font': '#414651',
	'grey-border': '#D5D7DA',
	'white-13': '#F3F8FF',
	'white-12': '#E1E4E8',
	'spinner-border-color': '#d7e7f4',
	'spinner-border-top-color': '#aecfe9',
	'gradient-color-01': '#0050b2',
	'gradient-color-02': '#0055be',
	'gradient-color-03': '#005bcb',
	'gradient-color-04': '#0060d7',
	'gradient-color-05': '#0066E4',
};

interface ColorConfigItem {
	id: number;
	color_name: string;
	color_code_hex: string;
	description?: string;
}

@Injectable({
	providedIn: 'root',
})
export class ThemeConfigurationService {
	private _configData = signal<ColorConfigItem[]>([]);
	private _loadObs$: Observable<void> | null = null;

	private _colorMap = computed(() => {
		const apiData = this._configData().reduce((acc, item) => {
			acc[item.color_name] = item.color_code_hex;
			return acc;
		}, {} as Record<string, string>);

		return { ...DEFAULT_THEME, ...apiData };
	});

	constructor(private _apiService: ApiService) {
		effect(() => {
			const colors = this._colorMap();
			this.applyColorsToDom(colors);
		});
	}

	private applyColorsToDom(colors: Record<string, string>): void {
		const root = document.documentElement;
		Object.entries(colors).forEach(([name, value]) => {
			root.style.setProperty(`--${name}`, value);
		});
		this.applyFrameworkTokens(root, colors);
	}

	/**
	 * Ionic and Angular Material ship their own fixed primary colours
	 * (--ion-color-primary, --mdc-*). Point them at the configured theme so
	 * every button/checkbox/spinner/ion element follows the API config too.
	 */
	private applyFrameworkTokens(root: HTMLElement, colors: Record<string, string>): void {
		const primary = colors['primary-color'];
		if (!primary) {
			return;
		}
		const rgb = this.hexToRgb(primary);
		const tokens: Record<string, string> = {
			'--primary-color-hover': `color-mix(in srgb, ${primary}, black 10%)`,
			// Ionic
			'--ion-color-primary': primary,
			'--ion-color-primary-contrast': '#ffffff',
			'--ion-color-primary-shade': `color-mix(in srgb, ${primary}, black 12%)`,
			'--ion-color-primary-tint': `color-mix(in srgb, ${primary}, white 10%)`,
			// Angular Material (MDC tokens)
			'--mdc-filled-button-container-color': primary,
			'--mdc-filled-button-label-text-color': '#ffffff',
			'--mdc-text-button-label-text-color': primary,
			'--mdc-outlined-button-label-text-color': primary,
			'--mdc-checkbox-selected-icon-color': primary,
			'--mdc-checkbox-selected-focus-icon-color': primary,
			'--mdc-checkbox-selected-hover-icon-color': primary,
			'--mdc-checkbox-selected-pressed-icon-color': primary,
			'--mdc-circular-progress-active-indicator-color': primary,
			'--mdc-outlined-text-field-focus-outline-color': primary,
			'--mdc-outlined-text-field-focus-label-text-color': primary,
			'--mdc-outlined-text-field-caret-color': primary,
			'--mat-select-focused-arrow-color': primary,
			'--mat-option-selected-state-label-text-color': primary,
			'--mdc-switch-selected-handle-color': primary,
			'--mdc-switch-selected-track-color': `color-mix(in srgb, ${primary}, white 55%)`,
			'--mat-datepicker-calendar-date-selected-state-background-color': primary,
		};
		if (rgb) {
			tokens['--ion-color-primary-rgb'] = rgb;
		}
		Object.entries(tokens).forEach(([name, value]) => root.style.setProperty(name, value));
	}

	private hexToRgb(hex: string): string | null {
		const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());
		if (!m) {
			return null;
		}
		let h = m[1];
		if (h.length === 3) {
			h = h.split('').map((c) => c + c).join('');
		}
		const n = parseInt(h, 16);
		return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
	}

	public ensureConfigurationLoaded(): Observable<void> {
		if (this._loadObs$) {
			return this._loadObs$;
		}

		this._loadObs$ = this._apiService.get(URLS.themeColorConfig).pipe(
			timeout(2500),
			map((response: any) => {
				this._configData.set(response?.data || []);
			}),
			catchError(() => {
				this._configData.set([]);
				return of(undefined);
			}),
			shareReplay(1),
		);

		return this._loadObs$;
	}
}
